import { useTranslation } from '@pancakeswap/localization'
import { Flex, Text, useMatchBreakpoints } from '@pancakeswap/uikit'
import useTheme from 'hooks/useTheme'
import { styled } from 'styled-components'

/* ---- Figma-accurate feature cards (283×218, 16px radius) ---- */

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.isDark ? theme.colors.backgroundAlt : '#ffffff'};
  border: 1px solid ${({ theme }) => theme.isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f7'};
  border-radius: 16px;
  padding: 20px 16px 18px;
  flex: 1;
  min-width: 0;

  @media (min-width: 480px) {
    padding: 28px 24px 24px;
    min-width: 240px;
  }
  text-align: center;
  position: relative;
  box-shadow:
    0 4px 6px -4px rgba(0, 0, 0, 0.1),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 6px 10px -4px rgba(0, 0, 0, 0.12),
      0 16px 24px -4px rgba(0, 0, 0, 0.12);
  }
`

/* 88px outer gradient square + 80px inner icon bg */
const IconOuter = styled.div<{ $gradient: string }>`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;

  @media (min-width: 480px) {
    width: 88px;
    height: 88px;
    border-radius: 16px;
    margin-bottom: 16px;
  }
`

const IconInner = styled.div<{ $gradient: string }>`
  width: 56px;
  height: 56px;

  @media (min-width: 480px) {
    width: 80px;
    height: 80px;
  }
  border-radius: 16px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
`

/* Lightning bolt icon */
const LightningIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22 3L7 23h11l-2 14 15-20H20l2-14z"
      fill="#F59E0B"
      stroke="#D97706"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
)

/* Shield / checkmark icon */
const ShieldIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 4L6 10v9c0 8.4 5.7 16.2 14 18 8.3-1.8 14-9.6 14-18v-9L20 4z"
      fill="#3CB371"
      stroke="#00A080"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 20l4 4 8-8"
      stroke="#fff"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/* Coin / dollar icon */
const CoinIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="16" fill="#E91E63" stroke="#BE185D" strokeWidth="1.5" />
    <circle cx="20" cy="20" r="11" fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.5" />
    <text
      x="20"
      y="25"
      textAnchor="middle"
      fill="#fff"
      fontSize="14"
      fontWeight="bold"
      fontFamily="Inter, sans-serif"
    >
      $
    </text>
  </svg>
)

const getFeatures = (isDark: boolean) => [
  {
    key: 'lightning',
    outerGradient: isDark ? 'rgba(245,158,11,0.12)' : 'linear-gradient(135deg, #FEF3C7 0%, #FDE9D4 100%)',
    innerGradient: isDark ? 'rgba(245,158,11,0.08)' : 'linear-gradient(135deg, #FFFBEB 0%, #FFF7ED 100%)',
    Icon: LightningIcon,
  },
  {
    key: 'shield',
    outerGradient: isDark ? 'rgba(95,203,130,0.12)' : 'linear-gradient(135deg, #DCFCE7 0%, #D0FAE5 100%)',
    innerGradient: isDark ? 'rgba(95,203,130,0.08)' : 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)',
    Icon: ShieldIcon,
  },
  {
    key: 'coin',
    outerGradient: isDark ? 'rgba(233,30,99,0.12)' : 'linear-gradient(135deg, #F3E8FF 0%, #FCE7F3 100%)',
    innerGradient: isDark ? 'rgba(233,30,99,0.08)' : 'linear-gradient(135deg, #FAF5FF 0%, #FDF2F8 100%)',
    Icon: CoinIcon,
  },
]

const EcoSystemSection: React.FC = () => {
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()
  const { theme } = useTheme()
  const features = getFeatures(theme.isDark)

  const texts = [
    { title: t('Lightning Fast'), desc: t('Sub-second finality. Your trades complete before you blink.') },
    { title: t('Battle Tested'), desc: t('Audited contracts. Your funds are always safe.') },
    { title: t('Zero Gas Stress'), desc: t('Near-zero fees on MA-Chain. More money in your pocket.') },
  ]

  return (
    <Flex justifyContent="center" alignItems="center" flexDirection="column" pt={isMobile ? '24px' : '60px'}>
      <Text textAlign="center" p="0px 20px" mb="8px">
        <Text fontSize={['28px', null, null, '32px']} lineHeight="110%" display="inline-block" bold color="text">
          {t('Why')}
        </Text>
        <Text
          fontSize={['28px', null, null, '32px']}
          ml="8px"
          display="inline-block"
          bold
          lineHeight="110%"
          color="secondary"
        >
          {t('MacSwap?')}
        </Text>
      </Text>
      <Text textAlign="center" fontSize="16px" color="textSubtle" mb="40px">
        {t('Built different. Trade different.')}
      </Text>

      <Flex
        width="100%"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="center"
        style={{ gap: isMobile ? '16px' : '24px' }}
      >
        {features.map((f, i) => (
          <FeatureCard key={f.key}>
            <IconOuter $gradient={f.outerGradient}>
              <IconInner $gradient={f.innerGradient}>
                <f.Icon />
              </IconInner>
            </IconOuter>
            <Text fontSize="16px" fontWeight={700} mb="8px" color="text" lineHeight="24px">
              {texts[i].title}
            </Text>
            <Text fontSize="14px" color="textSubtle" lineHeight="20px">
              {texts[i].desc}
            </Text>
          </FeatureCard>
        ))}
      </Flex>
    </Flex>
  )
}

export default EcoSystemSection
